import React from "react";
import {
  ExportableVisibility,
  PlayEvidenceExportResponse,
} from "../../../api/playAdminApi";

const RUNG_LABELS: Record<ExportableVisibility, string> = {
  admin_evidence_candidate: "Admin evidence candidate",
  governor_pack_approved_input: "Governor pack approved",
};

/**
 * The versioned evidence-export table. The Download button emits the
 * UNTRANSFORMED `play-evidence-export-v1` response body — the contract IS
 * the artefact (consumed by governor-pack tooling), so no reshaping here.
 * Only the two admin-readable rungs are filterable (the backend rejects
 * anything else).
 */
export const EvidenceExportPanel: React.FC<{
  visibility: ExportableVisibility | "";
  onVisibilityChange: (v: ExportableVisibility | "") => void;
  offset: number;
  onOffsetChange: (o: number) => void;
  data: PlayEvidenceExportResponse | null;
  loading: boolean;
}> = ({ visibility, onVisibilityChange, offset, onOffsetChange, data, loading }) => {
  const download = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `play-evidence-export-v1-${new Date()
      .toISOString()
      .slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      data-testid="evidence-export-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-gray-500">
          Evidence export (play-evidence-export-v1)
        </h3>
        <div className="flex items-center gap-2 text-sm">
          <select
            value={visibility}
            onChange={(e) =>
              onVisibilityChange(e.target.value as ExportableVisibility | "")
            }
            className="rounded-md border border-gray-300 px-2 py-1"
            data-testid="export-visibility-filter"
          >
            <option value="">Both admin-readable stages</option>
            {(
              Object.entries(RUNG_LABELS) as Array<[ExportableVisibility, string]>
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={download}
            disabled={!data || loading}
            className="rounded-md bg-[rgb(var(--pd-ink))] px-3 py-1.5 font-medium text-white disabled:bg-gray-300"
            data-testid="export-download"
          >
            Download JSON
          </button>
        </div>
      </div>

      {loading && <p className="mt-3 text-sm text-gray-400">Loading…</p>}
      {!loading && data && data.items.length === 0 && (
        <p className="mt-3 text-sm text-gray-400" data-testid="export-empty">
          No evidence at the admin-readable stages yet.
        </p>
      )}
      {!loading && data && data.items.length > 0 && (
        <>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1">Created</th>
                <th className="py-1">Workflow</th>
                <th className="py-1">Stage</th>
                <th className="py-1 text-right">AI used</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, i) => (
                <tr key={String(item.evidence_id ?? i)} className="border-t border-gray-100">
                  <td className="py-1.5 text-gray-600">
                    {typeof item.created_at === "string"
                      ? new Date(item.created_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="py-1.5 text-gray-700">
                    {String(
                      (item.workflow as { slug?: string } | undefined)?.slug ??
                        "—"
                    )}
                  </td>
                  <td className="py-1.5 text-gray-600">
                    {RUNG_LABELS[item.visibility_state as ExportableVisibility] ??
                      String(item.visibility_state ?? "—")}
                  </td>
                  <td className="py-1.5 text-right text-gray-600">
                    {item.ai_used === true ? "Yes" : item.ai_used === false ? "No" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
            <span>
              Showing {data.items.length} of {data.total}
            </span>
            {offset + data.limit < data.total && (
              <button
                type="button"
                className="rounded-md bg-gray-900 px-2 py-1 font-medium text-white"
                onClick={() => onOffsetChange(offset + data.limit)}
              >
                Next
              </button>
            )}
            {offset > 0 && (
              <button
                type="button"
                className="rounded-md border border-gray-300 px-2 py-1 font-medium text-gray-700"
                onClick={() => onOffsetChange(Math.max(0, offset - data.limit))}
              >
                Previous
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
